<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'

interface Props {
  mode?: 'fade' | 'slide' | 'slide-up' | 'scale' | 'none'
  duration?: number
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'fade',
  duration: 300
})

const route = useRoute()
const transitionName = ref('page-fade')

// Map mode to transition name
const modeToTransition: Record<string, string> = {
  fade: 'page-fade',
  slide: 'page-slide',
  'slide-up': 'page-slide-up',
  scale: 'page-scale',
  none: ''
}

watch(() => props.mode, (newMode) => {
  transitionName.value = modeToTransition[newMode] || 'page-fade'
}, { immediate: true })

// Optionally detect navigation direction for slide transitions
const previousPath = ref('')
const direction = ref<'forward' | 'backward'>('forward')

watch(() => route.path, (newPath, oldPath) => {
  if (oldPath) {
    previousPath.value = oldPath
    // Simple heuristic: deeper paths are "forward"
    direction.value = newPath.split('/').length > oldPath.split('/').length ? 'forward' : 'backward'
  }
})
</script>

<template>
  <router-view v-slot="{ Component, route: currentRoute }">
    <Transition
      :name="transitionName"
      mode="out-in"
      appear
    >
      <component
        :is="Component"
        :key="currentRoute.path"
      />
    </Transition>
  </router-view>
</template>

<style scoped>
/* Fade Transition */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity v-bind('duration + "ms"') ease;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}

/* Slide Transition */
.page-slide-enter-active,
.page-slide-leave-active {
  transition: all v-bind('duration + "ms"') cubic-bezier(0.4, 0, 0.2, 1);
}

.page-slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.page-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Slide Up Transition */
.page-slide-up-enter-active,
.page-slide-up-leave-active {
  transition: all v-bind('duration + "ms"') cubic-bezier(0.4, 0, 0.2, 1);
}

.page-slide-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-slide-up-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Scale Transition */
.page-scale-enter-active,
.page-scale-leave-active {
  transition: all v-bind('duration + "ms"') cubic-bezier(0.4, 0, 0.2, 1);
}

.page-scale-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.page-scale-leave-to {
  opacity: 0;
  transform: scale(1.02);
}
</style>
